import React, { useCallback, useMemo, useState } from 'react';
import { RenderBinderSelector } from 'src/components/knowledge-content/knowledge-content-editor/components/hub/render-binder-selector';
import { getLang } from 'shared/store/selectors/lang.selector';
import { getUser } from 'shared/store/selectors/user.selector';
import { useSelector } from 'react-redux';
import { STORE_ENTITY_TYPES } from 'shared/constants';
import { MultipleOwnersSelector } from 'src/components/owners/owners-selector/multiple-owners-selector';
import { notification } from 'antd';
import Logger from 'shared/utils/Logger';
import styles from './mirror-category-popup.module.scss';

const MirrorCategoryPopupComponent = React.memo(function MirrorCategoryPopupComponent({
  item = {},
  doMirror = () => {},
  onFinish = () => {},
  isOpen = false,
  generateBreadcrumbs,
  getCategoriesById,
  getCategoryById,
  getRoot,
  isCategoryMove = false,
  isSection = () => {},
  entity = STORE_ENTITY_TYPES.KNOWLEDGE,
  disabledIds = [],
}) {
  const lang = useSelector(getLang('KNOWLEDGE_MIRROR'));
  const currentUser = useSelector(getUser);
  const defaultOwnerIds = useMemo(
    () => (currentUser?.id ? [currentUser.id] : []),
    [currentUser?.id],
  );
  const [targetCategoryId, setTargetCategoryId] = useState(null);
  const [targetWorkspaceId, setTargetWorkspaceId] = useState(null);
  const [ownerIds, setOwnerIds] = useState(defaultOwnerIds);

  // Reset state when popup opens — default the owner to the current user.
  React.useEffect(() => {
    if (isOpen) {
      setOwnerIds(defaultOwnerIds);
      setTargetWorkspaceId(null);
      setTargetCategoryId(null);
    }
  }, [isOpen, defaultOwnerIds]);

  const handleWorkspaceChange = useCallback(
    workspaceId => {
      setTargetWorkspaceId(workspaceId);
      setOwnerIds(defaultOwnerIds);
    },
    [defaultOwnerIds],
  );

  const selectBinder = useCallback(binderAndWorkspace => {
    setTargetCategoryId(binderAndWorkspace?.binderId);
    if (binderAndWorkspace?.targetWorkspaceId) {
      setTargetWorkspaceId(binderAndWorkspace.targetWorkspaceId);
    }
  }, []);

  const dismiss = useCallback(() => {
    setTargetCategoryId(null);
    onFinish();
  }, [onFinish]);

  const handleOwnerChange = useCallback((_name, value) => {
    setOwnerIds(value || []);
  }, []);

  const handleMirror = useCallback(async () => {
    try {
      await doMirror({
        sourceCategoryId: item.id,
        targetCategoryId,
        ownerIds,
      });

      notification.success({
        message: lang.SUCCESS_MESSAGE,
        description: lang.SUCCESS_DESCRIPTION,
        placement: 'bottomRight',
        duration: 8,
      });
    } catch (error) {
      Logger.error('Mirror failed:', { error });
      notification.error({
        message: lang.ERROR_MESSAGE,
        placement: 'bottomRight',
      });
      throw error;
    }
  }, [doMirror, item, targetCategoryId, ownerIds, lang]);

  return (
    <RenderBinderSelector
      title={lang.TITLE}
      subtitle={item.title}
      submitButtonText={lang.MIRROR}
      selectText={lang.SELECT}
      selectedText={lang.SELECTED}
      selectRootText={lang.SELECT_ROOT}
      selectTarget={selectBinder}
      onSubmit={handleMirror}
      isOpen={isOpen}
      onClose={dismiss}
      getRoot={getRoot}
      getCategoriesById={getCategoriesById}
      getCategoryById={getCategoryById}
      generateBreadcrumbs={generateBreadcrumbs}
      isSection={isSection}
      entity={entity}
      isCategoryMove={isCategoryMove}
      cannotMoveToBinderText={lang.CANNOT_MIRROR_TO_BINDER}
      blockMirroredDestinations={true}
      submitDisabled={ownerIds.length === 0}
      onWorkspaceChange={handleWorkspaceChange}
      defaultWorkspaceId={null}
      excludeWorkspaceIds={[]}
      filterCategories={disabledIds}
      alreadyInCategoryText={lang.CANNOT_MIRROR_TO_THIS_LOCATION}
      pathParts={item?.path?.split(',')}>
      {targetWorkspaceId && (
        <>
          <MultipleOwnersSelector
            label={lang.OWNER}
            size="large"
            value={ownerIds}
            error={targetCategoryId && ownerIds.length === 0 ? lang.OWNER_REQUIRED : null}
            placeholder={lang.OWNER_PLACEHOLDER}
            name="ownerIds"
            onChange={handleOwnerChange}
            borderRadius={4}
            required
            workspaceId={targetWorkspaceId}
          />
          <p className={styles.ownerHelp}>{lang.OWNER_HELP}</p>
        </>
      )}
    </RenderBinderSelector>
  );
});

export { MirrorCategoryPopupComponent };
